import React from 'react';
import { Box, Typography, Table, TableHead, TableBody, TableRow, TableCell, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

// Italian Cookie Policy section bodies (binding version). Carries the FULL
// substance of docs/legal/cookie-policy.md §§1-8 — layout differs (MUI
// tables/lists instead of markdown tables/bullets) but no sentence is
// dropped, except two purely self-referential ones documented in
// task-C-report.md's fix-round-2 section ("this text is also published on
// this page" / "the current version is published at this address" — both
// moot while reading this very page). Keys match `cookies.page.sections.*`.
function Tbl({ head, rows }) {
  return (
    <Table size="small" sx={{ mb: 2, '& td, & th': { verticalAlign: 'top', fontSize: '0.85rem' } }}>
      <TableHead>
        <TableRow>
          {head.map((h) => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>
            {row.map((cell, j) => <TableCell key={j}>{cell}</TableCell>)}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const cookieIt = {
  titolare: (
    <>
      <strong>[DENOMINAZIONE E FORMA GIURIDICA]</strong> — sede in <strong>[INDIRIZZO]</strong> — email{' '}
      <code>info@fluel.it</code> — contatto privacy <strong><code>[privacy@fluel.it]</code></strong>. Per ogni
      informazione sul trattamento dei dati personali si rinvia all'<strong>
        <Link component={RouterLink} to="/privacy">Informativa Privacy</Link>
      </strong>.
    </>
  ),
  cosaSono: (
    <>
      <Typography sx={{ mb: 2 }}>
        I <strong>cookie</strong> sono piccoli file di testo che i siti visitati inviano al terminale dell'utente,
        dove vengono memorizzati per essere ritrasmessi agli stessi siti alla visita successiva. Strumenti
        analoghi (es. <em>local storage</em>, <em>session storage</em>, pixel, SDK, identificatori) permettono di
        conservare o accedere a informazioni sul dispositivo dell'utente e sono soggetti alla stessa disciplina.
      </Typography>
      <Typography sx={{ mb: 1 }}>In base alla finalità si distinguono:</Typography>
      <Box component="ul" sx={{ mb: 2 }}>
        <li><strong>Cookie/strumenti tecnici</strong> — necessari a far funzionare il sito e a erogare un
          servizio espressamente richiesto dall'utente (es. autenticazione, sicurezza, bilanciamento del
          carico, memorizzazione delle preferenze). <strong>Non richiedono il consenso</strong> (art. 122,
          comma 1, Codice Privacy).</li>
        <li><strong>Cookie/strumenti di misurazione (analytics)</strong> — usati per raccogliere informazioni
          aggregate sull'uso del sito. Sono assimilati ai tecnici <strong>solo se</strong> opportunamente
          anonimizzati e di sola prima parte, <strong>senza incrocio con altri trattamenti né trasmissione a
          terzi</strong>; <strong>altrimenti richiedono il consenso</strong>.</li>
        <li><strong>Cookie/strumenti di profilazione e marketing</strong> — creano profili dell'utente per
          inviare messaggi in linea con le preferenze manifestate. <strong>Richiedono sempre il consenso</strong>{' '}
          preventivo, libero, specifico, informato e revocabile.</li>
      </Box>
      <Typography>
        In base al gestore si distinguono cookie <strong>di prima parte</strong> (impostati dal Titolare) e{' '}
        <strong>di terza parte</strong> (impostati da soggetti diversi).
      </Typography>
    </>
  ),
  strumentiUsati: (
    <>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>3.1 Strumenti tecnici (nessun consenso richiesto)</Typography>
      <Tbl
        head={['Strumento', 'Tipo / tecnologia', 'Finalità', 'Durata', 'Gestore']}
        rows={[
          [
            'Token di autenticazione Firebase',
            'localStorage / sessionStorage (strumento analogo ai cookie)',
            "Mantenere l'utente autenticato tra le pagine e le sessioni; sicurezza dell'accesso",
            'Fino a logout o scadenza del token; i dati in sessionStorage si cancellano alla chiusura del browser',
            'Google (per conto del Titolare)',
          ],
          [
            "Preferenze dell'interfaccia",
            'localStorage',
            "Ricordare impostazioni non essenziali scelte dall'utente (es. stato di sezioni)",
            'Persistente fino a cancellazione',
            'Titolare',
          ],
          [
            'Record dello stato del consenso (sg.consent)',
            'localStorage (non un cookie)',
            'Memorizzare le scelte espresse sul banner cookie (categoria "Statistiche" accettata/rifiutata)',
            '12 mesi, poi il banner si ripropone automaticamente',
            'Titolare',
          ],
          [
            'Caratteri tipografici (Montserrat, Rajdhani, Fraunces, Bricolage Grotesque, DM Sans)',
            'File statici (.woff2) serviti dal dominio del Servizio',
            'Visualizzazione corretta della tipografia del sito',
            'Cache del browser',
            'Titolare — i font sono ospitati sul dominio del Servizio, nessuna richiesta né comunicazione dell\'indirizzo IP a Google o ad altri terzi',
          ],
        ]}
      />

      <Typography sx={{ fontWeight: 700, mb: 1, mt: 3 }}>3.2 Strumenti soggetti a consenso, attivabili dall'utente</Typography>
      <Tbl
        head={['Strumento', 'Finalità', 'Dati / effetti', 'Consenso', 'Riferimenti']}
        rows={[
          [
            'Google Analytics 4 (ID G-FXG3L4ZPD9) — cookie _ga, _ga_<ID>, durata fino a 2 anni',
            "Statistiche sull'uso del sito",
            "Misurazione dell'uso del sito; gestore Google Ireland Ltd / Google LLC; trasferimento dei dati negli Stati Uniti nell'ambito dell'EU-US Data Privacy Framework e, dove applicabile, delle Clausole Contrattuali Tipo",
            'Richiesto — attivato solo previo consenso espresso sul banner (categoria "Statistiche"); se il consenso viene revocato, la raccolta viene disattivata lato client, i cookie _ga/_ga_<ID> vengono eliminati e, con il successivo caricamento della pagina, lo script non viene più caricato affatto',
            <Link key="ga-ref" href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Informativa privacy di Google</Link>,
          ],
        ]}
      />

      <Typography sx={{ fontWeight: 700, mb: 1, mt: 3 }}>3.3 Strumenti NON attualmente attivi (previsti per il futuro)</Typography>
      <Typography sx={{ mb: 2 }}>
        I seguenti strumenti <strong>non sono attivi</strong> alla data odierna. Saranno attivati <strong>solo
        previa raccolta del consenso</strong>, dove richiesto, e questa pagina sarà contestualmente aggiornata:
      </Typography>
      <Tbl
        head={['Strumento', 'Finalità', 'Categoria', 'Consenso']}
        rows={[
          [
            'Stripe (js.stripe.com)',
            'Elaborazione dei pagamenti e prevenzione frodi, se e quando saranno attivi gli abbonamenti',
            'Tecnico per la funzione di pagamento; alcune componenti antifrode possono essere soggette a consenso',
            "Da valutare all'attivazione",
          ],
        ]}
      />
    </>
  ),
  baseGiuridica: (
    <Box component="ul">
      <li>Per gli <strong>strumenti tecnici</strong>: l'art. 122, comma 1, Codice Privacy, che ne consente
        l'uso senza consenso in quanto necessari all'erogazione del servizio richiesto; il trattamento dei dati
        personali eventualmente connesso si fonda sul legittimo interesse del Titolare a garantire
        funzionamento e sicurezza (art. 6.1.f GDPR) o sull'esecuzione del contratto (art. 6.1.b).</li>
      <li>Per gli <strong>strumenti di misurazione non anonimizzati, di profilazione e marketing</strong>: il{' '}
        <strong>consenso</strong> dell'utente (art. 122 Codice Privacy; art. 6.1.a GDPR), raccolto tramite
        banner e revocabile in ogni momento.</li>
    </Box>
  ),
  gestirePreferenze: (
    <>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>5.1 Sul sito</Typography>
      <Typography sx={{ mb: 2 }}>
        Al primo accesso compare un <strong>banner di consenso</strong>, con tre scelte di pari evidenza —{' '}
        <strong>Accetta tutti</strong>, <strong>Rifiuta</strong>, <strong>Personalizza</strong> — oltre a una
        "X" di chiusura che, al primo accesso, equivale a Rifiuta (se il banner viene invece riaperto dal link
        "Preferenze cookie" quando una scelta è già registrata, chiudere con "X" o "Esc" non modifica il
        consenso esistente). Nessuna casella è pre-selezionata: la categoria "Statistiche (Google Analytics)"
        è disattivata finché l'utente non la attiva esplicitamente da "Personalizza". Il rifiuto non limita
        l'accesso ai contenuti. La scelta è memorizzata (v. § 3.1, sg.consent) per 12 mesi, dopodiché il banner
        si ripropone. In qualsiasi momento è possibile <strong>modificare le scelte</strong> tramite il link
        "Preferenze cookie" nel footer del sito, che riapre il banner.
      </Typography>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>5.2 Dal browser</Typography>
      <Typography sx={{ mb: 1 }}>
        L'utente può bloccare o eliminare i cookie e i dati dei siti dalle impostazioni del browser. La
        disattivazione degli strumenti tecnici può compromettere l'uso del sito (in particolare
        l'autenticazione). Istruzioni:
      </Typography>
      <Box component="ul" sx={{ mb: 1 }}>
        <li><Link href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</Link></li>
        <li><Link href="https://support.mozilla.org/it/kb/Gestione%20dei%20cookie" target="_blank" rel="noopener noreferrer">Mozilla Firefox</Link></li>
        <li><Link href="https://support.apple.com/it-it/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Apple Safari</Link></li>
        <li><Link href="https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">Microsoft Edge</Link></li>
      </Box>
      <Typography>
        Per cancellare il localStorage/sessionStorage: funzione "Cancella dati di navigazione" / "Dati dei
        siti web" del browser.
      </Typography>
    </>
  ),
  trasferimenti: (
    <>
      L'uso di strumenti di terze parti (es. Google) può comportare il trasferimento di dati verso Paesi
      terzi. Tali trasferimenti avvengono sulla base delle garanzie indicate nell'<strong>
        <Link component={RouterLink} to="/privacy">Informativa Privacy</Link>
      </strong> (§ 6), cui si rinvia (decisioni di adeguatezza, incluso l'EU-US Data Privacy Framework ove
      applicabile, o Clausole Contrattuali Tipo con misure supplementari).
    </>
  ),
  diritti: (
    <>
      <Typography sx={{ mb: 1 }}>
        L'utente può esercitare i diritti previsti dagli artt. 15-22 GDPR (
        <strong>accesso, rettifica, cancellazione, limitazione, opposizione, portabilità</strong>) e revocare
        il consenso in qualsiasi momento, scrivendo a <strong><code>[privacy@fluel.it]</code></strong>.
      </Typography>
      <Typography>
        Ha inoltre diritto di proporre reclamo al <strong>Garante per la protezione dei dati personali</strong> (
        <Link href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">www.garanteprivacy.it</Link>
        ). Per il dettaglio si rinvia all'<Link component={RouterLink} to="/privacy">Informativa Privacy</Link>.
      </Typography>
    </>
  ),
  aggiornamenti: (
    <>
      <Typography sx={{ mb: 1 }}>
        La presente Cookie Policy può essere modificata in caso di introduzione di nuovi strumenti o di
        evoluzioni normative; in caso di attivazione di strumenti soggetti a consenso, la modifica sarà
        accompagnata dalla riproposizione del banner.
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Titolare: <strong>[DENOMINAZIONE]</strong>. Si veda anche l'<Link component={RouterLink} to="/privacy">Informativa Privacy</Link> e i Termini e Condizioni di Servizio.
      </Typography>
    </>
  ),
};

export default cookieIt;
